import React, { useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useGetPurchaseOrderDetail } from "../../../hooks/api/usePurchaseOrder";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  PORTAL_PREFIX,
  PAGE_PORTAL_ORDERS_PURCHASE_LIST,
  PAGE_PORTAL_ORDERS_INVOICE_DETAIL,
  PAGE_PORTAL_FINANCE_RECEIPT_DETAIL
} from "@kivunova/kivufrontendcommon";
import PrintButton from "../../common/PrintButton/PrintButton";
import PrintableContent from "../../common/PrintableContent/PrintableContent";
import NoPurchaseOrderDetailFound from "./NoPurchaseOrderDetailFound";
import EditPurchaseOrderModal from "./EditPurchaseOrderModal";
import DeletePurchaseOrderModal from "./DeletePurchaseOrderModal";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DescriptionIcon from "@mui/icons-material/Description";
import { formatDate, getStatusColor } from "../utils/orderUtils";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import { FALLBACK_CURRENCY_CODE } from "../../../constants/currency";
import { OrderItemEntryStatus } from "../enums/orderItemEntryStatus";
import { OrderStatus } from "../enums/orderStatus";
import { OrderDocumentType } from "../enums/orderDocumentType";
import AddSalesOrderModal from "../SalesOrder/AddSalesOrderModal";
import { PaymentStatus } from "../../Payment/enums/paymentStatus";
import { PaymentMethodLabels } from "../../Payment/enums/paymentMethod";
import { useReportError } from "../../../hooks/useReportError";
import OpaqueIdOrText from "../../common/OpaqueIdOrText";

const PurchaseOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const formatCurrency = useFormatCurrency();
  const { purchaseOrderDetail } = useSelector(
    (state: RootState) => state.purchaseOrders
  );
  const { invoiceList } = useSelector((state: RootState) => state.invoices);
  const { receiptList } = useSelector((state: RootState) => state.receipts);
  const useGetPurchaseOrderDetailMutation = useGetPurchaseOrderDetail();
  const reportError = useReportError();
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openDelete, setOpenDelete] = React.useState(false);
  const [openCreateSalesOrder, setOpenCreateSalesOrder] = React.useState(false);
  const printContentRef = useRef<HTMLDivElement>(null);

  // Find invoice linked to this order
  const linkedInvoice = React.useMemo(() => {
    if (!purchaseOrderDetail) return null;

    const invoiceLink = purchaseOrderDetail.linkedDocuments?.find(
      (link) =>
        link.documentType === OrderDocumentType.INVOICE &&
        link.relationship === "target"
    );

    if (invoiceLink) {
      return invoiceList.find((inv) => inv.id === invoiceLink.documentId);
    }

    return invoiceList.find(
      (inv) =>
        inv.orderId === purchaseOrderDetail.id &&
        inv.orderType === OrderDocumentType.PURCHASE_ORDER
    );
  }, [purchaseOrderDetail, invoiceList]);

  // Find receipts for the linked invoice
  const invoiceReceipts = React.useMemo(() => {
    if (!linkedInvoice) return [];
    return receiptList.filter(
      (receipt) => receipt.invoiceId === linkedInvoice.id
    );
  }, [linkedInvoice, receiptList]);

  useEffect(() => {
    if (orderId) {
      (async () => {
        try {
          await useGetPurchaseOrderDetailMutation.mutateAsync({
            orderId
          });
        } catch (err) {
          reportError("Failed to fetch purchase order detail (UI)", err, {
            component: "PurchaseOrderDetail",
            orderId: orderId ?? ""
          });
        }
      })();
    }
  }, [orderId, reportError]);

  if (!purchaseOrderDetail || purchaseOrderDetail.id !== orderId) {
    return <NoPurchaseOrderDetailFound />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent={{ xs: "space-between", md: "flex-end" }}
        alignItems="center"
        mb={3}
        gap={2}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(
              localizedPath(
                `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PURCHASE_LIST}`,
                language,
                defaultLang
              )
            )
          }
          sx={{
            color: BRAND_COLOR,
            display: { xs: "flex", md: "none" },
            textTransform: "none"
          }}
        >
          {t("purchase-order-detail-back-button", "back to purchase orders")}
        </Button>
        <Box display="flex" gap={2}>
          {linkedInvoice && (
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              onClick={() => {
                const route = PAGE_PORTAL_ORDERS_INVOICE_DETAIL.replace(
                  ":invoiceId",
                  linkedInvoice.id
                );
                navigate(
                  localizedPath(
                    `/${PORTAL_PREFIX}/${route}`,
                    language,
                    defaultLang
                  )
                );
              }}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": {
                  backgroundColor: BRAND_HOVER
                },
                textTransform: "none"
              }}
            >
              {t("purchase-order-detail-view-invoice-button", "View Invoice")}
            </Button>
          )}
          {!linkedInvoice &&
            (purchaseOrderDetail.status === OrderStatus.CONFIRMED ||
              purchaseOrderDetail.status === OrderStatus.COMPLETED ||
              purchaseOrderDetail.status === OrderStatus.OPEN) && (
              <Button
                variant="contained"
                startIcon={<ShoppingBagIcon />}
                onClick={() => setOpenCreateSalesOrder(true)}
                sx={{
                  backgroundColor: BRAND_COLOR,
                  "&:hover": {
                    backgroundColor: BRAND_HOVER
                  },
                  textTransform: "none"
                }}
              >
                {t(
                  "purchase-order-detail-create-sales-order-button",
                  "Create Sales Order"
                )}
              </Button>
            )}
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setOpenEdit(true)}
            sx={{
              backgroundColor: "white",
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_COLOR,
                opacity: 0.8,
                color: "white"
              },
              textTransform: "none"
            }}
          >
            {t("purchase-order-detail-edit-button", "Edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={() => setOpenDelete(true)}
            sx={{
              textTransform: "none"
            }}
          >
            {t("purchase-order-detail-delete-button", "Delete")}
          </Button>
          <PrintButton
            targetRef={printContentRef}
            title={`${t("purchase-order-detail-print-title", "Purchase Order")}: ${purchaseOrderDetail.documentNumber}`}
            label={t("purchase-order-detail-print-button", "Print Order")}
          />
        </Box>
      </Box>

      {/* Printable Content */}
      <PrintableContent
        id="purchase-order-printable-content"
        printTitle={`${t("purchase-order-detail-print-title", "Purchase Order")}: ${purchaseOrderDetail.documentNumber}`}
        printSubtitle={purchaseOrderDetail.documentNumber}
        ref={printContentRef}
      >
        {/* Order Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h4" fontWeight="bold">
              {purchaseOrderDetail.documentNumber}
            </Typography>
            <Chip
              label={purchaseOrderDetail.status}
              color={getStatusColor(purchaseOrderDetail.status)}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("purchase-order-detail-supplier-label", "Supplier:")}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {purchaseOrderDetail.supplierName ? (
                  <Typography variant="body2" fontWeight="medium">
                    {purchaseOrderDetail.supplierName}
                  </Typography>
                ) : (
                  <OpaqueIdOrText value={purchaseOrderDetail.supplierId} />
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("purchase-order-detail-created-label", "Created:")}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(purchaseOrderDetail.createdAt)}
              </Typography>
            </Box>

            {purchaseOrderDetail.expectedDeliveryDate && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "purchase-order-detail-expected-delivery-label",
                    "Expected Delivery:"
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(purchaseOrderDetail.expectedDeliveryDate)}
                </Typography>
              </Box>
            )}

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("purchase-order-detail-subtotal-label", "Subtotal:")}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(
                  purchaseOrderDetail.subtotal,
                  purchaseOrderDetail.currency || FALLBACK_CURRENCY_CODE
                )}
              </Typography>
            </Box>

            {purchaseOrderDetail.tax && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("purchase-order-detail-tax-label", "Tax:")}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(
                    purchaseOrderDetail.tax,
                    purchaseOrderDetail.currency || FALLBACK_CURRENCY_CODE
                  )}
                </Typography>
              </Box>
            )}

            {purchaseOrderDetail.shippingCost && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("purchase-order-detail-shipping-label", "Shipping:")}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(
                    purchaseOrderDetail.shippingCost,
                    purchaseOrderDetail.currency || FALLBACK_CURRENCY_CODE
                  )}
                </Typography>
              </Box>
            )}

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold">
                {t("purchase-order-detail-total-label", "Total:")}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(
                  purchaseOrderDetail.totalAmount,
                  purchaseOrderDetail.currency || FALLBACK_CURRENCY_CODE
                )}
              </Typography>
            </Box>

            {purchaseOrderDetail.notes && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {t("purchase-order-detail-notes-label", "Notes:")}
                  </Typography>
                  <Typography variant="body2">
                    {purchaseOrderDetail.notes}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {t("purchase-order-detail-line-items-title", "Line Items")}
          </Typography>
          {purchaseOrderDetail.lineItems &&
          purchaseOrderDetail.lineItems.length > 0 ? (
            <Box>
              {purchaseOrderDetail.lineItems.map((lineItem, index) => {
                const soldEntries = lineItem.itemEntries.filter(
                  (entry) => entry.status === OrderItemEntryStatus.SOLD
                );
                const reservedEntries = lineItem.itemEntries.filter(
                  (entry) => entry.status === OrderItemEntryStatus.RESERVED
                );
                const voidedEntries = lineItem.itemEntries.filter(
                  (entry) => entry.status === OrderItemEntryStatus.VOIDED
                );

                return (
                  <Accordion
                    key={lineItem.id}
                    defaultExpanded={index === 0}
                    sx={{
                      mb: 1,
                      "&:before": {
                        display: "none"
                      },
                      boxShadow: 1,
                      "&.Mui-expanded": {
                        margin: "0 0 8px 0"
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "background.default",
                        "&:hover": {
                          backgroundColor: "action.hover"
                        }
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        pr={2}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {lineItem.productName}
                        </Typography>
                        <Box display="flex" gap={3} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {t("purchase-order-detail-qty-label", "Qty")}:{" "}
                            {lineItem.quantity}
                            {lineItem.unit ? ` ${lineItem.unit}` : ""}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(
                              lineItem.totalPrice,
                              purchaseOrderDetail.currency ||
                                FALLBACK_CURRENCY_CODE
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {/* Sold Items */}
                        {soldEntries.length > 0 && (
                          <Box
                            sx={{
                              mb: 2,
                              pl: 2,
                              borderLeft: `3px solid ${BRAND_COLOR}`
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                              mb={1}
                            >
                              {t(
                                "purchase-order-detail-sold-items-label",
                                "Sold Items"
                              )}{" "}
                              ({soldEntries.length}):
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                              {soldEntries.map((entry) => (
                                <Box
                                  key={entry.id}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    component="a"
                                    href={localizedPath(
                                      `/${PORTAL_PREFIX}/inventory/products/${lineItem.productId}/items/${entry.productItemId}`,
                                      language,
                                      defaultLang
                                    )}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(
                                        localizedPath(
                                          `/${PORTAL_PREFIX}/inventory/products/${lineItem.productId}/items/${entry.productItemId}`,
                                          language,
                                          defaultLang
                                        )
                                      );
                                    }}
                                    sx={{
                                      color: BRAND_COLOR,
                                      textDecoration: "none",
                                      cursor: "pointer",
                                      fontWeight: "medium",
                                      "&:hover": {
                                        textDecoration: "underline"
                                      }
                                    }}
                                    variant="body2"
                                  >
                                    {entry.productItemId}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    •{" "}
                                    {t(
                                      "purchase-order-detail-qty-label",
                                      "Qty"
                                    )}
                                    : {entry.quantity}
                                    {lineItem.unit ? ` ${lineItem.unit}` : ""}
                                  </Typography>
                                  {entry.soldAt && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      •{" "}
                                      {t(
                                        "purchase-order-detail-sold-at-label",
                                        "Sold"
                                      )}
                                      : {formatDate(entry.soldAt)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Reserved Items */}
                        {reservedEntries.length > 0 && (
                          <Box
                            sx={{
                              mb: 2,
                              pl: 2,
                              borderLeft: "3px solid #ff9800"
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                              mb={1}
                            >
                              {t(
                                "purchase-order-detail-reserved-items-label",
                                "Reserved Items"
                              )}{" "}
                              ({reservedEntries.length}):
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                              {reservedEntries.map((entry) => (
                                <Box
                                  key={entry.id}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    component="a"
                                    href={localizedPath(
                                      `/${PORTAL_PREFIX}/inventory/products/${lineItem.productId}/items/${entry.productItemId}`,
                                      language,
                                      defaultLang
                                    )}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(
                                        localizedPath(
                                          `/${PORTAL_PREFIX}/inventory/products/${lineItem.productId}/items/${entry.productItemId}`,
                                          language,
                                          defaultLang
                                        )
                                      );
                                    }}
                                    sx={{
                                      color: "#ff9800",
                                      textDecoration: "none",
                                      cursor: "pointer",
                                      fontWeight: "medium",
                                      "&:hover": {
                                        textDecoration: "underline"
                                      }
                                    }}
                                    variant="body2"
                                  >
                                    {entry.productItemId}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    •{" "}
                                    {t(
                                      "purchase-order-detail-qty-label",
                                      "Qty"
                                    )}
                                    : {entry.quantity}
                                    {lineItem.unit ? ` ${lineItem.unit}` : ""}
                                  </Typography>
                                  {entry.reservedAt && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      •{" "}
                                      {t(
                                        "purchase-order-detail-reserved-at-label",
                                        "Reserved"
                                      )}
                                      : {formatDate(entry.reservedAt)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Voided Items */}
                        {voidedEntries.length > 0 && (
                          <Box
                            sx={{
                              mb: 2,
                              pl: 2,
                              borderLeft: "3px solid #9e9e9e"
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.secondary"
                              mb={1}
                            >
                              {t(
                                "purchase-order-detail-voided-items-label",
                                "Voided Items"
                              )}{" "}
                              ({voidedEntries.length}):
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                              {voidedEntries.map((entry) => (
                                <Box
                                  key={entry.id}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textDecoration: "line-through" }}
                                  >
                                    {entry.productItemId}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    •{" "}
                                    {t(
                                      "purchase-order-detail-qty-label",
                                      "Qty"
                                    )}
                                    : {entry.quantity}
                                    {lineItem.unit ? ` ${lineItem.unit}` : ""}
                                  </Typography>
                                  {entry.voidedAt && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      •{" "}
                                      {t(
                                        "purchase-order-detail-voided-at-label",
                                        "Voided"
                                      )}
                                      : {formatDate(entry.voidedAt)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* No items message */}
                        {lineItem.itemEntries.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              "purchase-order-detail-no-items-scanned",
                              "No items scanned yet"
                            )}
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("purchase-order-detail-no-line-items", "No line items")}
            </Typography>
          )}
        </Paper>

        {/* Receipts Section */}
        {linkedInvoice &&
          (linkedInvoice.paymentStatus === PaymentStatus.PARTIAL ||
            linkedInvoice.paymentStatus === PaymentStatus.PAID) &&
          invoiceReceipts.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {t("purchase-order-detail-receipts-title", "Receipts")} (
                {invoiceReceipts.length})
              </Typography>
              <Stack spacing={2}>
                {invoiceReceipts.map((receipt) => (
                  <Box
                    key={receipt.id}
                    sx={{
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      }
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography
                          component="a"
                          href={localizedPath(
                            `/${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_RECEIPT_DETAIL.replace(
                              ":receiptId",
                              receipt.id
                            )}`,
                            language,
                            defaultLang
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            const route =
                              PAGE_PORTAL_FINANCE_RECEIPT_DETAIL.replace(
                                ":receiptId",
                                receipt.id
                              );
                            navigate(
                              localizedPath(
                                `/${PORTAL_PREFIX}/${route}`,
                                language,
                                defaultLang
                              )
                            );
                          }}
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            color: BRAND_COLOR,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                        >
                          {receipt.receiptNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("purchase-order-detail-issued-label", "Issued")}:{" "}
                          {formatDate(receipt.issuedDate)}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        gap={0.5}
                      >
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(
                            receipt.amount,
                            receipt.currency ||
                              purchaseOrderDetail.currency ||
                              FALLBACK_CURRENCY_CODE
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {PaymentMethodLabels[receipt.paymentMethod] ||
                            receipt.paymentMethod}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
      </PrintableContent>

      {/* Edit Modal */}
      <EditPurchaseOrderModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        purchaseOrder={purchaseOrderDetail}
      />

      {/* Delete Modal */}
      <DeletePurchaseOrderModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        purchaseOrder={purchaseOrderDetail}
      />

      {/* Create Sales Order Modal */}
      <AddSalesOrderModal
        open={openCreateSalesOrder}
        onClose={() => setOpenCreateSalesOrder(false)}
        purchaseOrderId={purchaseOrderDetail?.id}
        purchaseOrderStatus={purchaseOrderDetail?.status}
        purchaseOrderType={OrderDocumentType.PURCHASE_ORDER}
      />
    </Box>
  );
};

export default PurchaseOrderDetail;
